import type { NormalizedLandmarks, Point3D } from '../types';

/**
 * Position Smoother
 *
 * Applies exponential moving average (EMA) to landmark positions
 * to reduce jitter and provide stable output.
 */
export class LandmarkSmoother {
  private previousLandmarks: NormalizedLandmarks | null = null;
  private smoothFactor: number;

  constructor(smoothFactor: number = 0.3) {
    this.smoothFactor = Math.max(0.01, Math.min(1.0, smoothFactor));
  }

  setSmoothFactor(factor: number): void {
    this.smoothFactor = Math.max(0.01, Math.min(1.0, factor));
  }

  getSmoothFactor(): number {
    return this.smoothFactor;
  }

  smooth(currentLandmarks: NormalizedLandmarks | null): NormalizedLandmarks | null {
    if (!currentLandmarks) {
      return null;
    }

    if (!this.previousLandmarks) {
      this.previousLandmarks = this.cloneLandmarks(currentLandmarks);
      return currentLandmarks;
    }

    if (this.previousLandmarks.points.length !== currentLandmarks.points.length) {
      this.previousLandmarks = this.cloneLandmarks(currentLandmarks);
      return currentLandmarks;
    }

    const smoothedPoints = this.smoothPoints(
      this.previousLandmarks.points,
      currentLandmarks.points
    );

    const smoothedLandmarks: NormalizedLandmarks = {
      points: smoothedPoints,
      confidence: currentLandmarks.confidence
    };

    if (currentLandmarks.regions && this.previousLandmarks.regions) {
      smoothedLandmarks.regions = {};

      for (const [regionName, currentRegion] of Object.entries(currentLandmarks.regions)) {
        const previousRegion = this.previousLandmarks.regions[regionName as keyof typeof this.previousLandmarks.regions];

        if (currentRegion && previousRegion && currentRegion.length === previousRegion.length) {
          smoothedLandmarks.regions[regionName as keyof typeof smoothedLandmarks.regions] = this.smoothPoints(previousRegion, currentRegion);
        } else if (currentRegion) {
          smoothedLandmarks.regions[regionName as keyof typeof smoothedLandmarks.regions] = currentRegion;
        }
      }
    } else if (currentLandmarks.regions) {
      smoothedLandmarks.regions = currentLandmarks.regions;
    }

    this.previousLandmarks = this.cloneLandmarks(smoothedLandmarks);

    return smoothedLandmarks;
  }

  private smoothPoints(previous: Point3D[], current: Point3D[]): Point3D[] {
    const smoothed: Point3D[] = [];

    for (let i = 0; i < current.length; i++) {
      const prev = previous[i];
      const curr = current[i];

      const x = prev.x * (1 - this.smoothFactor) + curr.x * this.smoothFactor;
      const y = prev.y * (1 - this.smoothFactor) + curr.y * this.smoothFactor;
      const z = prev.z * (1 - this.smoothFactor) + curr.z * this.smoothFactor;

      smoothed.push({ x, y, z });
    }

    return smoothed;
  }

  reset(): void {
    this.previousLandmarks = null;
  }

  private cloneLandmarks(landmarks: NormalizedLandmarks): NormalizedLandmarks {
    const cloned: NormalizedLandmarks = {
      points: landmarks.points.map(p => ({ ...p })),
      confidence: landmarks.confidence
    };

    if (landmarks.regions) {
      cloned.regions = {};
      for (const [regionName, points] of Object.entries(landmarks.regions)) {
        if (points) {
          cloned.regions[regionName as keyof typeof cloned.regions] = points.map(p => ({ ...p }));
        }
      }
    }

    return cloned;
  }
}
